import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Mail, Lock } from 'lucide-react';
import { Button } from '@/components/ui';
import { GradientDots } from '@/components/ui/GradientDots';
import { OperatorWordmark } from '@/components/auth/OperatorWordmark';
import { GlowInput } from '@/components/auth/GlowInput';

export type SignInStep = 'email' | 'password' | 'redirecting';

export interface SignInViewProps {
  step: SignInStep;
  email: string;
  password: string;
  /** Plain string for simple errors, or the sentinel 'cookies-too-large'
   *  to render the rich CloudFront-431 recovery variant. */
  error: string;
  /** One-shot confirmation banner (e.g. shown after /signin/reset clears
   *  cookies). Rendered in place of the error slot when no error is set. */
  notice?: string;
  isLoading: boolean;
  signupEnabled: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onEmailSubmit: (e: React.FormEvent) => void;
  onPasswordSubmit: (e: React.FormEvent) => void;
  onUseDifferentEmail: () => void;
}

const fadeMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.25, ease: 'easeOut' as const },
};

export function SignInView({
  step,
  email,
  password,
  error,
  notice,
  isLoading,
  signupEnabled,
  onEmailChange,
  onPasswordChange,
  onEmailSubmit,
  onPasswordSubmit,
  onUseDifferentEmail,
}: SignInViewProps) {
  return (
    <div
      className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12"
      style={{ backgroundColor: 'hsl(var(--auth-bg))' }}
    >
      <GradientDots />

      <div
        className="relative z-10 w-full max-w-3xl rounded-[32px] border border-foreground/15 bg-card/95 dark:bg-card/15 p-6 shadow-[0_80px_160px_-30px_rgba(0,0,0,0.8),0_40px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-3xl sm:p-12 md:p-16 lg:p-20 2xl:p-28 2xl:max-w-4xl"
      >
        <div className="flex flex-col items-center text-center">
          <OperatorWordmark size="xl" />
          <p className="mt-6 text-[10px] tracking-[0.15em] sm:text-xs sm:tracking-[0.25em] uppercase text-muted-foreground">
            Make AI Actually Do Things
          </p>
        </div>

        <div className="mt-10 min-h-[248px]">
          {step === 'redirecting' ? (
            <motion.div
              {...fadeMotion}
              className="flex min-h-[248px] items-center justify-center text-center text-sm text-muted-foreground"
            >
              Redirecting to your identity provider…
            </motion.div>
          ) : (
            <form
              onSubmit={step === 'password' ? onPasswordSubmit : onEmailSubmit}
              className="space-y-5"
              noValidate
            >
              {/* Header slot — email row appears in password step. Padded
                  px-5 to align with the input's text indent. */}
              <div className="min-h-[24px]">
                <AnimatePresence initial={false}>
                  {step === 'password' && (
                    <motion.div
                      key="header"
                      {...fadeMotion}
                      className="flex items-center justify-between px-5 text-sm"
                    >
                      <span className="truncate text-foreground">{email}</span>
                      <button
                        type="button"
                        onClick={onUseDifferentEmail}
                        className="ml-2 shrink-0 font-medium text-primary-600 hover:text-primary-500 focus-ring rounded-sm"
                      >
                        Use a different email
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Input slot — swaps between email and password */}
              <AnimatePresence mode="wait" initial={false}>
                {step === 'email' ? (
                  <motion.div key="email-input" {...fadeMotion}>
                    <GlowInput
                      type="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => onEmailChange(e.target.value)}
                      placeholder="Enter email address"
                      aria-label="Email address"
                      icon={<Mail />}
                    />
                  </motion.div>
                ) : (
                  <motion.div key="password-input" {...fadeMotion}>
                    <GlowInput
                      type="password"
                      autoComplete="current-password"
                      autoFocus
                      value={password}
                      onChange={(e) => onPasswordChange(e.target.value)}
                      placeholder="Enter password"
                      aria-label="Password"
                      icon={<Lock />}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Footer slot — error (left) + forgot password (right).
                  Padded px-5 to align with the input's text indent. Stacks
                  vertically on mobile so error text and the forgot link
                  don't collide in the same row. */}
              <div className="flex min-h-[24px] flex-col gap-1 px-5 text-sm sm:flex-row sm:items-start sm:justify-between">
                <AnimatePresence initial={false}>
                  {error === 'cookies-too-large' ? (
                    <motion.span
                      key="error-cookies"
                      {...fadeMotion}
                      role="alert"
                      className="text-[hsl(335_85%_70%)]"
                    >
                      Too many cookies stored for this site.{' '}
                      <Link
                        to="/signin/reset"
                        className="font-medium text-primary-600 underline underline-offset-2 hover:text-primary-500"
                      >
                        Clear cookies &amp; try again
                      </Link>
                    </motion.span>
                  ) : error ? (
                    <motion.span
                      key="error"
                      {...fadeMotion}
                      role="alert"
                      className="text-[hsl(335_85%_70%)]"
                    >
                      {error}
                    </motion.span>
                  ) : notice ? (
                    <motion.span
                      key="notice"
                      {...fadeMotion}
                      role="status"
                      className="text-muted-foreground"
                    >
                      {notice}
                    </motion.span>
                  ) : null}
                </AnimatePresence>
                <AnimatePresence initial={false}>
                  {step === 'password' && (
                    <motion.div key="forgot" {...fadeMotion} className="sm:ml-auto">
                      <Link
                        to="/forgot-password"
                        state={{ email }}
                        className="font-medium text-primary-600 hover:text-primary-500"
                      >
                        Forgot your password?
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Button
                type="submit"
                className="auth-button-hue w-full text-white border shadow-lg bg-[hsl(252_8%_20%)] border-[hsl(252_10%_32%)] hover:bg-[hsl(252_10%_26%)] hover:border-[hsl(252_12%_40%)]"
                size="lg"
                isLoading={isLoading}
              >
                {step === 'password' ? 'Sign in' : 'Continue'}
              </Button>

              {/* Signup link slot — kept at constant height when signup is
                  enabled so the card doesn't shrink (and the inputs don't
                  visually shift) when transitioning email → password. */}
              {signupEnabled && (
                <div className="min-h-[20px] text-center text-sm">
                  <AnimatePresence initial={false}>
                    {step === 'email' && (
                      <motion.p
                        key="signup"
                        {...fadeMotion}
                        className="text-muted-foreground"
                      >
                        Don't have an account?{' '}
                        <Link
                          to="/signup"
                          className="font-medium text-primary-600 hover:text-primary-500"
                        >
                          Create one
                        </Link>
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Cross-device sign-in — for shared / hard-to-type screens
                  (car browsers, TVs): show a QR code, approve from a phone. */}
              <div className="min-h-[20px] text-center text-sm">
                <AnimatePresence initial={false}>
                  {step === 'email' && (
                    <motion.p
                      key="device-signin"
                      {...fadeMotion}
                      className="text-muted-foreground"
                    >
                      On a shared or in-car screen?{' '}
                      <Link
                        to="/signin/device"
                        className="font-medium text-primary-600 hover:text-primary-500"
                      >
                        Sign in with your phone
                      </Link>
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            </form>
          )}
        </div>

        {/* Terms — centered in the gap between the form and the dialog border */}
        <p className="mt-10 text-center text-xs text-muted-foreground">
          By continuing, you agree to our{' '}
          <a
            href="/terms"
            className="underline underline-offset-2 hover:text-foreground"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="/privacy"
            className="underline underline-offset-2 hover:text-foreground"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
